import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DownloadReportModal = ({ onClose, onGenerate, isGenerating = false }) => {
  const [dateRange, setDateRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [includeIdentity, setIncludeIdentity] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [includeSessionHistory, setIncludeSessionHistory] = useState(true);
  const [includeWellnessHistory, setIncludeWellnessHistory] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Validate custom date range if selected
    if (dateRange === 'custom') {
      if (!customStartDate) {
        newErrors.customStartDate = 'Start date is required';
      }
      if (!customEndDate) {
        newErrors.customEndDate = 'End date is required';
      }
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        if (start > end) {
          newErrors.customEndDate = 'End date must be after start date';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleGenerate = () => {
    if (!validateForm()) return;

    let startDate, endDate;

    if (dateRange === 'custom') {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      // Calculate date range from preset
      const end = new Date();
      const start = new Date();
      start?.setDate(end?.getDate() - parseInt(dateRange));
      startDate = start?.toISOString()?.split('T')?.[0];
      endDate = end?.toISOString()?.split('T')?.[0];
    }

    const reportConfig = {
      startDate,
      endDate,
      reportDate: endDate, // Report date is the end date
      includeIdentity,
      name: includeIdentity ? name : null,
      email: includeIdentity ? email : null,
      includeSessionHistory,
      includeWellnessHistory
    };

    onGenerate(reportConfig);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Download Report</h2>
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="p-1 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={20} color="var(--color-text-secondary)" strokeWidth={2} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Date Range Selector */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Report Date Range
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setDateRange('30')}
                disabled={isGenerating}
                className={`w-full px-4 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  dateRange === '30' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-text-secondary border-border hover:bg-muted'
                }`}
              >
                Last 30 days
              </button>
              <button
                onClick={() => setDateRange('90')}
                disabled={isGenerating}
                className={`w-full px-4 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  dateRange === '90' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-text-secondary border-border hover:bg-muted'
                }`}
              >
                Last 90 days
              </button>
              <button
                onClick={() => setDateRange('custom')}
                disabled={isGenerating}
                className={`w-full px-4 py-2 text-sm rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  dateRange === 'custom' ?'bg-primary text-primary-foreground border-primary' :'bg-background text-text-secondary border-border hover:bg-muted'
                }`}
              >
                Custom date range
              </button>
            </div>

            {/* Custom Date Inputs */}
            {dateRange === 'custom' && (
              <div className="mt-4 space-y-3 p-3 bg-muted rounded-lg">
                <Input
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e?.target?.value)}
                  error={errors?.customStartDate}
                  disabled={isGenerating}
                />
                <Input
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e?.target?.value)}
                  error={errors?.customEndDate}
                  disabled={isGenerating}
                />
              </div>
            )}
          </div>

          {/* Optional Identity Fields */}
          <div className="space-y-3">
            <Checkbox
              checked={includeIdentity}
              onChange={(e) => setIncludeIdentity(e?.target?.checked)}
              label="Include name and email on report"
              disabled={isGenerating}
            />

            {includeIdentity && (
              <div className="space-y-3 p-3 bg-muted rounded-lg">
                <Input
                  type="text"
                  label="Name (optional)"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e?.target?.value)}
                  disabled={isGenerating}
                />
                <Input
                  type="email"
                  label="Email (optional)"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  disabled={isGenerating}
                />
              </div>
            )}
          </div>

          {/* Table Options */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Report Tables
            </label>
            <div className="space-y-2">
              <Checkbox
                checked={includeSessionHistory}
                onChange={(e) => setIncludeSessionHistory(e?.target?.checked)}
                label="Include Training Load Session History Table"
                disabled={isGenerating}
              />
              <Checkbox
                checked={includeWellnessHistory}
                onChange={(e) => setIncludeWellnessHistory(e?.target?.checked)}
                label="Include Wellness Score History Table"
                disabled={isGenerating}
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-surface border-t border-border px-6 py-4 flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            loading={isGenerating}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate PDF'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DownloadReportModal;